import { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Save, Upload, Trash2, Pencil, X, Plus, List,
  BarChart2, Sparkles, Package, Info, TriangleAlert, ZoomIn, ClipboardList,
} from "lucide-react";
import { fetchProductReviewDetails, updateProductDetails, uploadProductImage, fetchProductCategories } from "../services/productsService";
import { parseReviewFiles } from "../hooks/useReviewDetail";
import ConfirmModal from "../components/ConfirmModal";

const mapFileTypeToImageType = (fileType) => {
  const normalized = (fileType || "").toLowerCase();
  if (normalized.includes("front")) return "front_image";
  if (normalized.includes("barcode")) return "barcode_image";
  if (normalized.includes("ingredients") || normalized.includes("ingredient")) return "ingredients_image";
  if (normalized.includes("nutrition") || normalized.includes("nutrient")) return "nutrition_image";
  if (normalized.includes("back")) return "back_image";
  if (normalized.includes("allergen")) return "allergen_image";
  return "other_image";
};

const COMMON_NUTRIENT_SUGGESTIONS = [
  { key: "energy", label: "Energy", unit: "kcal" },
  { key: "fat", label: "Fat", unit: "g" },
  { key: "saturated_fat", label: "of which Saturates", unit: "g" },
  { key: "carbohydrate", label: "Carbohydrate", unit: "g" },
  { key: "sugar", label: "of which Sugars", unit: "g" },
  { key: "protein", label: "Protein", unit: "g" },
  { key: "sodium", label: "Sodium", unit: "mg" },
  { key: "fibre", label: "Dietary Fibre", unit: "g" },
  { key: "salt", label: "Salt", unit: "g" },
  { key: "calcium", label: "Calcium", unit: "mg" },
  { key: "iron", label: "Iron", unit: "mg" },
  { key: "vitamin_c", label: "Vitamin C", unit: "mg" },
  { key: "vitamin_d", label: "Vitamin D", unit: "mcg" },
];

const UNIT_SUGGESTIONS = ["g", "mg", "mcg", "kcal", "kJ", "ml", "IU", "%"];
const BASIS_SUGGESTIONS = ["100g", "100ml", "per serving"];

const AGE_GROUPS = [
  /* { key: "0_6_months", label: "0–6 Months" }, */  // commented: hide 0-24 months
  /* { key: "6_12_months", label: "6–12 Months" }, */ // commented: hide 0-24 months
  { key: "2_3_years", label: "2–3 Years" },
  { key: "4_6_years", label: "4–6 Years" },
  { key: "7_9_years", label: "7–9 Years" },
  { key: "10_12_years", label: "10–12 Years" },
];

const VALID_AGE_GROUP_KEYS = new Set(AGE_GROUPS.map((g) => g.key));

// product_score_v2 is keyed by scoring stage (C1–C4 cover 2–3/4–6/7–9/
// 10–12 years). Each bucket maps 1:1 with C1–C4.
const AGE_GROUP_STAGE_FALLBACK = {
  /* "0_6_months": ["I1", "I2", "I3"], */  // commented: hide 0-24 months
  /* "6_12_months": ["I1", "I2", "I3"], */ // commented: hide 0-24 months
  "2_3_years": ["C1"],
  "4_6_years": ["C2"],
  "7_9_years": ["C3"],
  "10_12_years": ["C4"],
  pregnancy: ["M1", "M"],
};

// Score card groups aligned with product_score_v2 stage ranges (C1–C4, M1).
// Labels match the view page's SCORE_STAGE_RANGES from ProductScoreStages.
const SCORE_CARD_GROUPS = [
  /* { keys: ["0_6_months", "6_12_months"], label: "0–24 Months" }, */ // commented: hide 0-24 months
  { keys: ["2_3_years"], label: "2–3 Years" },
  { keys: ["4_6_years"], label: "4–6 Years" },
  { keys: ["7_9_years"], label: "7–9 Years" },
  { keys: ["10_12_years"], label: "10–12 Years" },
  { keys: ["pregnancy"], label: "Pregnancy" },
];

// Youngest-to-oldest order index for every bucket key (pregnancy excluded —
// it isn't part of the age progression). Once a target Age Group is
// selected, any bracket younger than it is locked: age-group scores are
// expected to be non-decreasing, so a younger bracket shouldn't be hand-
// edited independently of the (older) target the score was actually set for.
const SCORE_ORDER_INDEX = {};
SCORE_CARD_GROUPS.forEach(({ keys }, i) => {
  if (keys[0] === "pregnancy") return;
  keys.forEach((key) => { SCORE_ORDER_INDEX[key] = i; });
});

// Which product_score_v2 stage each score card actually writes back to on
// save. The API stores scores by stage code, not by these age-group
// buckets, so saving must echo the full stage object back (band/flags/
// outcome/pillars unchanged) with only `score` replaced.
const SCORE_GROUP_TO_STAGE = {
  /* "0_6_months": "I1", */   // commented: hide 0-24 months
  /* "6_12_months": "I1", */  // commented: hide 0-24 months
  "2_3_years": "C1",
  "4_6_years": "C2",
  "7_9_years": "C3",
  "10_12_years": "C4",
  pregnancy: "M1",
};

// Used when a product has no product_score_v2 at all yet, so the payload
// still carries every stage the API expects (L1 included, even though it
// has no editable card of its own) rather than omitting the key.
const DEFAULT_PRODUCT_SCORE_V2 = {
  C1: { band: "C", flags: ["tree_nut", "sesame"], score: 49.5, outcome: "scored", pillars: { M: 25.3, N: 12.78, S: 11.38 } },
  C2: { band: "C", flags: ["tree_nut", "sesame"], score: 49.6, outcome: "scored", pillars: { M: 25.47, N: 12.78, S: 11.38 } },
  C3: { band: "C", flags: ["tree_nut", "sesame"], score: 49.8, outcome: "scored", pillars: { M: 25.64, N: 12.78, S: 11.38 } },
  C4: { band: "C", flags: ["tree_nut", "sesame"], score: 49.8, outcome: "scored", pillars: { M: 25.67, N: 12.78, S: 11.38 } },
  I1: { band: "C", flags: ["tree_nut", "sesame"], score: 48.4, outcome: "scored", pillars: { M: 24.27, N: 12.78, S: 11.38 } },
  L1: { band: "C", flags: ["tree_nut", "sesame"], score: 49.9, outcome: "scored", pillars: { M: 25.71, N: 12.78, S: 11.38 } },
  M1: { band: "C", flags: ["tree_nut", "sesame"], score: 49.9, outcome: "scored", pillars: { M: 25.71, N: 12.78, S: 11.38 } },
};

function normalizeAgeGroup(val) {
  if (!val || typeof val !== "string") return "";
  const cleaned = val.trim().toLowerCase().replace(/[\s–—-]+/g, "_");

  if (VALID_AGE_GROUP_KEYS.has(cleaned)) return cleaned;
  if (VALID_AGE_GROUP_KEYS.has(val)) return val;

  // Map common formats/variations — including the retired wider-bucket set
  // (1_3/3_5, 5_8/8_12 years) — onto the current stage-aligned buckets.
  // 0-24 months are commented out (hidden).
  // if (cleaned.includes("0_6") || ...) return "0_6_months"; // commented: hide 0-24 months
  // if (cleaned.includes("6_12") || ...) return "6_12_months"; // commented: hide 0-24 months
  if (cleaned.includes("2_3") || cleaned.includes("2_to_3") || cleaned.includes("1_3") || cleaned.includes("1_to_3") || cleaned.includes("1_2") || cleaned.includes("1_to_2")) return "2_3_years";
  if (cleaned.includes("4_6") || cleaned.includes("4_to_6") || cleaned.includes("3_5") || cleaned.includes("3_to_5")) return "4_6_years";
  if (cleaned.includes("7_9") || cleaned.includes("7_to_9") || cleaned.includes("5_8") || cleaned.includes("5_to_8") || cleaned.includes("5_7") || cleaned.includes("5_to_7")) return "7_9_years";
  if (cleaned.includes("10_12") || cleaned.includes("10_to_12") || cleaned.includes("8_12") || cleaned.includes("8_to_12") || cleaned.includes("7_10") || cleaned.includes("7_to_10") || cleaned.includes("7_12")) return "10_12_years";

  return "";
}

/** A score outside 0–100 (or non-numeric) is rejected by the backend. */
const isBadScore = (v) => v !== "" && (Number.isNaN(Number(v)) || Number(v) < 0 || Number(v) > 100);

const scoreColor = (v) => {
  const n = Number(v);
  if (v === "" || Number.isNaN(n)) return "#DBF5F0";
  if (n >= 80) return "#0C6170";
  if (n >= 60) return "#37BEB0";
  if (n >= 40) return "#f59e0b";
  return "#d94f4f";
};

/** Section card header used across every panel on this page. */
function SectionHead({ icon: Icon, title, subtitle, count, action }) {
  return (
    <div className="pw-sec-h">
      <div className="pw-sec-icn"><Icon size={15} /></div>
      <div className="flex-1 min-w-0">
        <div className="pw-sec-t">{title}</div>
        {subtitle && <div className="pw-sec-sub">{subtitle}</div>}
      </div>
      {count !== undefined && <span className="pw-count">{count}</span>}
      {action}
    </div>
  );
}

function Field({ label, required, optional, hint, error, children }) {
  return (
    <div>
      <label className="pw-lbl">
        {label}
        {required && <span style={{ color: "#d94f4f" }}>*</span>}
        {optional && <span className="pw-lbl-opt">optional</span>}
      </label>
      {children}
      {(error || hint) && <div className={`pw-hint${error ? " pw-hint-err" : ""}`}>{error || hint}</div>}
    </div>
  );
}

export default function ProductEditPage() {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { products, setProducts, addToast } = useOutletContext();

  const original = products.find((x) => x.id === productId);
  const [form, setForm] = useState(original ? { ...original } : null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Structured Ingredients & Nutrition Facts state
  const [ingredientsList, setIngredientsList] = useState([]);
  const [nutritionFactsList, setNutritionFactsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ageGroupScores, setAgeGroupScores] = useState({
    /* "0_6_months": "", */  // commented: hide 0-24 months
    /* "6_12_months": "", */ // commented: hide 0-24 months
    "2_3_years": "",
    "4_6_years": "",
    "7_9_years": "",
    "10_12_years": "",
    pregnancy: "",
  });

  const [imgI, setImgI] = useState(0);
  const [imgLoading, setImgLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmM, setConfirmM] = useState({ open: false, title: "", message: "", onConfirm: null, variant: "" });
  const [photos, setPhotosState] = useState([]);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Tracks unsaved edits so the header can warn and the browser can prompt.
  const [dirty, setDirty] = useState(false);
  // Ingredient ids whose (optional) concern note is expanded.
  const [openConcerns, setOpenConcerns] = useState({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const detail = await fetchProductReviewDetails(productId, original?.ageGroup);
        if (cancelled) return;

        if (detail) {
          setDetail(detail);
          const sub = detail.submission || {};
          const gtin = sub.gtin || detail.gtin || original?.gtin || "N/A";

          // 1. Initialize structured ingredients
          const ocrIngredients = detail.ocr?.ingredients || detail.ingredients || [];
          if (Array.isArray(ocrIngredients) && ocrIngredients.length > 0) {
            setIngredientsList(
              [...ocrIngredients]
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((ing, i) => ({
                  id: `ing-${i}-${Date.now()}`,
                  name: ing.name || "",
                  quantity: ing.quantity || "",
                  detail_concern: ing.detail_concern || "",
                }))
            );
          } else if (sub.ingredients || original?.ingredients) {
            const raw = sub.ingredients || original?.ingredients || "";
            const items = typeof raw === "string" ? raw.split(",").map(s => s.trim()).filter(Boolean) : (Array.isArray(raw) ? raw : []);
            setIngredientsList(
              items.map((item, i) => ({
                id: `ing-${i}-${Date.now()}`,
                name: typeof item === "string" ? item : (item.name || ""),
                quantity: item.quantity || "",
                detail_concern: item.detail_concern || "",
              }))
            );
          } else {
            setIngredientsList([{ id: `ing-0-${Date.now()}`, name: "", quantity: "", detail_concern: "" }]);
          }

          // 2. Initialize structured nutrition facts
          const rawNutritionFacts = detail.nutrition_facts || detail.ocr?.nutrition_facts || [];
          let normalizedNutrients = [];
          if (Array.isArray(rawNutritionFacts)) {
            normalizedNutrients = rawNutritionFacts;
          } else if (typeof rawNutritionFacts === "object" && rawNutritionFacts !== null) {
            normalizedNutrients = Object.entries(rawNutritionFacts).map(([k, v]) => ({
              nutrient_key: k,
              value: v?.value ?? v,
              unit: v?.unit || "g",
              basis: v?.basis || "100g",
              detail_concern: v?.detail_concern || null,
            }));
          }

          if (normalizedNutrients.length > 0) {
            const seen = new Set();
            const unique = [];
            for (const n of normalizedNutrients) {
              if (n && n.nutrient_key && !seen.has(n.nutrient_key)) {
                seen.add(n.nutrient_key);
                unique.push(n);
              }
            }

            setNutritionFactsList(
              unique.map((row, i) => ({
                id: `nutr-${i}-${Date.now()}`,
                nutrient_key: row.nutrient_key || "",
                label: row.nutrient_key ? row.nutrient_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "",
                value: row.value != null ? String(row.value) : "",
                unit: row.unit || "g",
                basis: row.basis || "100g",
                detail_concern: row.detail_concern || "",
              }))
            );
          } else {
            // Default common nutrients if none available
            setNutritionFactsList([
              { id: "1", nutrient_key: "energy", label: "Energy", value: "", unit: "kcal", basis: "100g" },
              { id: "2", nutrient_key: "fat", label: "Fat", value: "", unit: "g", basis: "100g" },
              { id: "3", nutrient_key: "carbohydrate", label: "Carbohydrate", value: "", unit: "g", basis: "100g" },
              { id: "4", nutrient_key: "sugar", label: "of which Sugars", value: "", unit: "g", basis: "100g" },
              { id: "5", nutrient_key: "protein", label: "Protein", value: "", unit: "g", basis: "100g" },
              { id: "6", nutrient_key: "sodium", label: "Sodium", value: "", unit: "mg", basis: "100g" },
            ]);
          }

          // 3. Initialize health scores for all age groups
          const rawScores = detail.age_group_scores || detail.submission?.age_group_scores || {};
          const scoreV2 = detail.product_score_v2 || detail.submission?.product_score_v2 || {};
          const singleScore = detail.product_score ?? detail.submission?.product_score;
          const rawAgeGroup = detail.age_group || detail.submission?.age_group || original?.ageGroup || "";
          const normalizedAgeGroup = normalizeAgeGroup(rawAgeGroup);

          const initialScores = {};
          SCORE_CARD_GROUPS.flatMap(({ keys }) => keys).forEach((key) => {
            if (rawScores[key] != null) {
              initialScores[key] = String(rawScores[key]);
            } else if (normalizedAgeGroup === key && singleScore != null) {
              initialScores[key] = String(singleScore);
            } else if (singleScore != null && Object.keys(rawScores).length === 0) {
              initialScores[key] = String(singleScore);
            } else {
              const stageEntry = (AGE_GROUP_STAGE_FALLBACK[key] || [])
                .map((stageKey) => scoreV2[stageKey])
                .find((entry) => entry != null);
              const stageScore = stageEntry && typeof stageEntry === "object" ? stageEntry.score : stageEntry;
              initialScores[key] = stageScore != null ? String(stageScore) : "";
            }
          });
          setAgeGroupScores(initialScores);

          setForm({
            id: productId,
            product_id: productId,
            name: sub.product_name || original?.name || "Unknown Product",
            brand: sub.brand_name || original?.brand || "Unknown Brand",
            category: sub.category || detail.category || original?.category || "",
            gtin: gtin,
            status: detail.status || original?.status || "pending",
            emoji: original?.emoji || "🔍",
            bg: original?.bg || "rgba(55,190,176,.12)",
            imageUrl: detail.image_url || sub.image_url || original?.imageUrl || null,
            createdDate: sub.submitted_at || detail.created_at || original?.createdDate,
            createdBy: sub.agent_id || "Agent",
            description: detail.description || original?.description || "",
            files: sub.files || [],
            isReview: original?.isReview || false,
            ageGroup: normalizedAgeGroup,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load product details");
          if (!original) {
            addToast(err.message || "Failed to load product details", "error");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [productId, original, addToast]);

  useEffect(() => {
    let cancelled = false;
    fetchProductCategories()
      .then((list) => { if (!cancelled) setCategories(list); })
      .catch(() => { /* category dropdown falls back to the product's existing value */ });
    return () => { cancelled = true; };
  }, []);

  // Initialize editable photos list
  useEffect(() => {
    if (photos.length === 0) {
      const parsed = parseReviewFiles(detail);
      if (parsed && parsed.length > 0) {
        setPhotosState(parsed);
      } else if (detail?.image_url) {
        setPhotosState([{
          fileId: "image_url",
          fileType: "front_label",
          fileName: "Front Image",
          url: detail.image_url,
          emoji: "📦",
          bg: "rgba(55,190,176,.12)",
        }]);
      } else if (original?.imageUrl) {
        setPhotosState([{
          fileId: "image_url",
          fileType: "front_label",
          fileName: "Front Image",
          url: original.imageUrl,
          emoji: "📦",
          bg: "rgba(55,190,176,.12)",
        }]);
      } else if (form) {
        setPhotosState([
          { emoji: form.emoji || "📦", bg: form.bg || "rgba(55,190,176,.12)" },
          { emoji: "📋",    bg: "rgba(55,190,176,.12)" },
          { emoji: "🏷️",    bg: "rgba(12,97,112,.1)" },
        ]);
      }
    }
  }, [detail, original, form, photos.length]);

  const imgs = photos;

  // Set loading to true when carousel index or image URL changes
  useEffect(() => {
    if (imgs[imgI]?.url) {
      setImgLoading(true);
    } else {
      setImgLoading(false);
    }
  }, [imgI, imgs]);

  // Warn on tab close / reload while there are unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Esc closes the fullscreen photo preview.
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen]);

  if (loading) {
    return (
      <div className="page-enter" style={{ paddingTop: 22 }}>
        <div className="pw-skel" style={{ height: 28, width: 220, marginBottom: 10 }} />
        <div className="pw-skel" style={{ height: 12, width: 320, marginBottom: 26 }} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 items-start">
          {[0, 1].map((col) => (
            <div key={col} className="flex flex-col gap-4">
              {[0, 1].map((c) => (
                <div key={c} className="pw-card">
                  <div className="pw-sec-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div className="pw-skel" style={{ height: 13, width: "38%" }} />
                    <div className="pw-skel" style={{ height: 38, borderRadius: 10 }} />
                    <div className="pw-skel" style={{ height: 38, borderRadius: 10 }} />
                    <div className="pw-skel" style={{ height: 38, borderRadius: 10, width: "70%" }} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="page-enter">
        <div className="empty-st">
          <TriangleAlert size={36} color="#fca5a5" />
          <div style={{ fontWeight: 700, color: "#d94f4f", marginBottom: 4 }}>Failed to load product</div>
          <div style={{ fontSize: 13, color: "#0C6170" }}>{error}</div>
          <button className="btn btn-g btn-sm" style={{ marginTop: 14 }} onClick={() => navigate("/products")}>
            <ChevronLeft size={14} />Back to Products
          </button>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="page-enter">
        <div className="empty-st">
          <Package size={36} color="#A4E5E0" />
          <div style={{ fontWeight: 700, color: "#0C6170" }}>Product not found</div>
        </div>
      </div>
    );
  }

  // ── Form helpers ────────────────────────────────────────────────────────
  const patchForm = (patch) => {
    setDirty(true);
    setForm((p) => ({ ...p, ...patch }));
  };

  const setScore = (keys, value) => {
    setDirty(true);
    setAgeGroupScores((p) => {
      const next = { ...p };
      keys.forEach((key) => { next[key] = value; });
      return next;
    });
  };

  // ── Ingredients Table Handlers ──────────────────────────────────────────
  const addIngredient = () => {
    setDirty(true);
    setIngredientsList(prev => [...prev, { id: `ing-${Date.now()}`, name: "", quantity: "", detail_concern: "" }]);
  };

  const updateIngredient = (id, field, value) => {
    setDirty(true);
    setIngredientsList(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeIngredient = (id) => {
    setDirty(true);
    setIngredientsList(prev => prev.filter(item => item.id !== id));
  };

  // ── Nutrition Facts Table Handlers ──────────────────────────────────────
  const addNutrient = (preset) => {
    setDirty(true);
    setNutritionFactsList(prev => [
      ...prev,
      preset
        ? { id: `nutr-${Date.now()}`, nutrient_key: preset.key, label: preset.label, value: "", unit: preset.unit, basis: "100g" }
        : { id: `nutr-${Date.now()}`, nutrient_key: "", label: "", value: "", unit: "g", basis: "100g" }
    ]);
  };

  const updateNutrient = (id, field, value) => {
    setDirty(true);
    setNutritionFactsList(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      if (field === "label") {
        // Auto-suggest key & unit if standard nutrient matched
        const lower = value.trim().toLowerCase();
        const matched = COMMON_NUTRIENT_SUGGESTIONS.find(s => s.label.toLowerCase() === lower || s.key === lower);
        if (matched) {
          updated.nutrient_key = matched.key;
          if (!item.unit || item.unit === "g") updated.unit = matched.unit;
        } else {
          updated.nutrient_key = lower.replace(/\s+/g, "_");
        }
      }
      return updated;
    }));
  };

  const removeNutrient = (id) => {
    setDirty(true);
    setNutritionFactsList(prev => prev.filter(item => item.id !== id));
  };

  // ── Save Action ────────────────────────────────────────────────────────
  const doSave = async () => {
    setSaving(true);
    try {
      // 1. Upload any locally replaced images in parallel before saving product details
      const uploadPromises = photos.map(async (photo, idx) => {
        if (photo.fileObject) {
          let imageType = "other_image";
          if (photo.fileType) {
            imageType = mapFileTypeToImageType(photo.fileType);
          } else {
            if (idx === 0) imageType = "front_image";
            else if (idx === 1) imageType = "ingredients_image";
            else if (idx === 2) imageType = "barcode_image";
          }

          const res = await uploadProductImage(productId, photo.fileObject, imageType);
          if (res && res.success && res.data?.url) {
            return {
              ...photo,
              url: res.data.url,
              fileObject: undefined, // upload complete
            };
          } else {
            throw new Error(`Failed to upload ${photo.fileName || 'image'}`);
          }
        }
        return photo;
      });

      const finalPhotos = await Promise.all(uploadPromises);
      setPhotosState(finalPhotos);

      // 2. Format structured nutrition facts object
      const nutritionFactsObj = {};
      nutritionFactsList.forEach((row) => {
        const key = row.nutrient_key || row.label.trim().toLowerCase().replace(/\s+/g, "_");
        if (key && row.value !== "") {
          nutritionFactsObj[key] = {
            value: isNaN(Number(row.value)) ? row.value : Number(row.value),
            unit: row.unit || "g",
            basis: row.basis || "100g",
            detail_concern: row.detail_concern || null,
          };
        }
      });

      // 3. Format structured ingredients array
      const cleanIngredients = ingredientsList
        .filter(i => i.name && i.name.trim())
        .map(i => ({
          name: i.name.trim(),
          quantity: i.quantity?.trim() || null,
          detail_concern: i.detail_concern?.trim() || null,
        }));

      // 4. Construct generated table_view.html so detail page continues to render rich HTML smoothly
      const generatedTableHtml = `<div class="back-label">
  <div class="ingredients-section">
    <h3>Ingredients</h3>
    <ul>
      ${cleanIngredients.map(ing => `<li>${ing.name}</li>`).join("\n      ")}
    </ul>
  </div>
  <div class="nutrition-section">
    <h3>Nutrition Facts</h3>
    <table>
      <thead>
        <tr>
          <th>Nutrient</th>
          <th>Value</th>
          <th>Unit</th>
          <th>Basis</th>
        </tr>
      </thead>
      <tbody>
        ${nutritionFactsList
          .filter(n => (n.label || n.nutrient_key) && n.value !== "")
          .map(n => `<tr>
          <td>${(n.nutrient_key || n.label).toLowerCase()}</td>
          <td>${n.value}</td>
          <td>${n.unit || 'g'}</td>
          <td>${n.basis || '100g'}</td>
        </tr>`).join("\n        ")}
      </tbody>
    </table>
  </div>
</div>`;

      const ageGroupScoresPayload = {};
      Object.entries(ageGroupScores).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) {
          ageGroupScoresPayload[k] = isNaN(Number(v)) ? v : Number(v);
        }
      });

      // product_score_v2 must be echoed back as received (band/flags/outcome/
      // pillars untouched) with only each edited stage's `score` replaced.
      // Falls back to a default full stage set when the product had none.
      const receivedScoreV2 = detail?.product_score_v2 || detail?.submission?.product_score_v2;
      const sourceScoreV2 = receivedScoreV2 && Object.keys(receivedScoreV2).length > 0 ? receivedScoreV2 : DEFAULT_PRODUCT_SCORE_V2;
      const productScoreV2Payload = {};
      Object.entries(sourceScoreV2).forEach(([stageKey, entry]) => {
        productScoreV2Payload[stageKey] = entry && typeof entry === "object" ? { ...entry } : { score: entry };
      });
      Object.entries(SCORE_GROUP_TO_STAGE).forEach(([groupKey, stageKey]) => {
        const val = ageGroupScores[groupKey];
        if (val === "" || val === null || val === undefined) return;
        const numVal = Number(val);
        if (Number.isNaN(numVal)) return;
        const existing = productScoreV2Payload[stageKey] || (DEFAULT_PRODUCT_SCORE_V2[stageKey] ? { ...DEFAULT_PRODUCT_SCORE_V2[stageKey] } : {});
        productScoreV2Payload[stageKey] = { ...existing, score: numVal };
      });

      const mainImageUrl = finalPhotos.find(p => p.fileType === "front_label")?.url || finalPhotos[0]?.url || form.imageUrl || "";
      const validAgeGroup = normalizeAgeGroup(form.ageGroup);
      const payload = {
        product_name: form.name,
        brand_name: form.brand,
        gtin: form.gtin || undefined,
        category: form.category || "General",
        description: form.description || "",
        image_url: mainImageUrl,
        age_group: validAgeGroup || undefined,
        age_group_scores: ageGroupScoresPayload,
        product_score_v2: productScoreV2Payload,
        ingredients: cleanIngredients,
        nutrition_facts: nutritionFactsObj,
        table_view: {
          html: generatedTableHtml,
        },
      };

      console.log("Save payload:", payload);
      await updateProductDetails(productId, payload);

      setProducts((prev) => prev.map((x) => (x.id === productId ? { ...form, imageUrl: mainImageUrl, ingredients: cleanIngredients.map(i => i.name).join(", ") } : x)));
      setDirty(false);
      addToast("Product saved successfully ✓", "success");
      navigate(`/products/${productId}`, { state: location.state });
    } catch (err) {
      addToast(err.message || "Failed to save product details", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReplaceThumbnailFile = (e) => {
    const file = e.target.files?.[0];
    if (file && selectedThumbnailIndex !== null) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotosState((prev) => {
          const next = [...prev];
          next[selectedThumbnailIndex] = {
            ...next[selectedThumbnailIndex],
            url: reader.result,   // Preview base64 data URL
            fileObject: file,     // Raw File object to be uploaded on Save
          };
          return next;
        });
        setDirty(true);
        setSelectedThumbnailIndex(null);
        addToast("Photo replaced locally ✓ (click Save to upload changes)", "info");
      };
      reader.readAsDataURL(file);
    }
    // Allow re-selecting the same file later
    e.target.value = "";
  };

  const handleDeleteThumbnail = () => {
    if (selectedThumbnailIndex !== null) {
      setPhotosState((prev) => {
        const next = [...prev];
        next[selectedThumbnailIndex] = {
          ...next[selectedThumbnailIndex],
          url: "", // clear URL
          fileObject: undefined, // clear local file object
        };
        return next;
      });
      setDirty(true);
      setSelectedThumbnailIndex(null);
      addToast("Photo deleted", "info");
    }
  };

  const handleSaveClick = () => {
    setConfirmM({
      open: true,
      title: "Save Changes",
      message: "Are you sure you want to save the changes to this product?",
      onConfirm: () => doSave(),
      variant: "success",
    });
  };

  const handleCancelClick = () => {
    if (!dirty) {
      navigate(`/products/${productId}`, { state: location.state });
      return;
    }
    setConfirmM({
      open: true,
      title: "Discard Changes",
      message: "You have unsaved changes. Are you sure you want to discard them and go back?",
      onConfirm: () => navigate(`/products/${productId}`, { state: location.state }),
      variant: "danger",
    });
  };

  // ── Derived validation / helpers ────────────────────────────────────────
  const invalidScoreCount = SCORE_CARD_GROUPS.filter(({ keys }) => isBadScore(ageGroupScores[keys[0]])).length;
  const namedIngredients = ingredientsList.filter((i) => i.name?.trim()).length;
  const concernCount = ingredientsList.filter((i) => i.detail_concern?.trim()).length;
  const usedNutrientKeys = new Set(nutritionFactsList.map((n) => n.nutrient_key));
  const nutrientPresets = COMMON_NUTRIENT_SUGGESTIONS.filter((s) => !usedNutrientKeys.has(s.key));
  const currentImg = imgs[imgI];
  const saveDisabled = saving || invalidScoreCount > 0 || !form.name?.trim() || !form.brand?.trim();

  return (
    <div className="page-enter pb-28 md:pb-8">
      {/* ── Sticky page header ─────────────────────────────────────────── */}
      <div className="pw-head">
        <div className="flex items-center justify-between gap-3 mb-3">
          <button className="btn btn-g btn-sm" onClick={handleCancelClick}>
            <ChevronLeft size={14} />Back
          </button>

          <div className="flex items-center gap-2.5">
            {dirty && <span className="pw-dot hidden sm:inline-flex">Unsaved changes</span>}
            <button className="btn btn-g btn-sm hidden md:inline-flex" onClick={handleCancelClick} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-p btn-sm hidden md:inline-flex" onClick={handleSaveClick} disabled={saveDisabled}>
              <Save size={13} />{saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="pw-eyebrow">Editing product</div>
        <h1 className="pw-title">{form.name || "Untitled product"}</h1>
        <div className="pw-chips">
          <span className="pw-chip"><Package size={12} /><span style={{ color: "#37BEB0" }}>Brand</span><b>{form.brand || "—"}</b></span>
          <span className="pw-chip pw-chip-mono"><ClipboardList size={12} /><span style={{ color: "#37BEB0" }}>GTIN</span><b>{form.gtin || "—"}</b></span>
          <span className="pw-chip"><List size={12} /><span style={{ color: "#37BEB0" }}>Ingredients</span><b>{namedIngredients}</b></span>
          <span className="pw-chip"><BarChart2 size={12} /><span style={{ color: "#37BEB0" }}>Nutrients</span><b>{nutritionFactsList.length}</b></span>
        </div>
      </div>

      {error && (
        <div className="pw-note pw-note-bad" style={{ marginTop: 16, display: "flex", gap: 9, alignItems: "flex-start" }}>
          <TriangleAlert size={15} color="#d94f4f" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13.5, color: "#b91c1c", lineHeight: 1.5 }}>{error}</div>
        </div>
      )}

      <div style={{ height: 18 }} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 items-start">
        {/* ── Left Column: Basic Info, Photos, Scores ─────────────────────── */}
        <div className="flex flex-col gap-4 min-w-0">

          {/* Basic Information */}
          <div className="pw-card">
            <SectionHead icon={Info} title="Basic Information" subtitle="Identity shown across the app" />
            <div className="pw-sec-b">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Field
                    label="Product Name"
                    required
                    error={!form.name?.trim() ? "Product name is required." : null}
                  >
                    <input
                      className={`inp${!form.name?.trim() ? " inp-err" : ""}`}
                      value={form.name}
                      onChange={(e) => patchForm({ name: e.target.value })}
                      placeholder="e.g. Organic Rice Cereal"
                    />
                  </Field>
                </div>

                <Field label="Brand" required error={!form.brand?.trim() ? "Brand is required." : null}>
                  <input
                    className={`inp${!form.brand?.trim() ? " inp-err" : ""}`}
                    value={form.brand}
                    onChange={(e) => patchForm({ brand: e.target.value })}
                    placeholder="e.g. Slurrp Farm"
                  />
                </Field>

                <Field label="GTIN / Barcode">
                  <input
                    className="inp"
                    value={form.gtin || ""}
                    onChange={(e) => patchForm({ gtin: e.target.value })}
                    placeholder="e.g. 8906002348152"
                    style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                  />
                </Field>

                <Field label="Category">
                  <select
                    className="inp"
                    value={form.category || ""}
                    onChange={(e) => patchForm({ category: e.target.value })}
                    style={{ cursor: "pointer" }}
                  >
                    <option value="">Select category</option>
                    {form.category && !categories.includes(form.category) && (
                      <option value={form.category}>{form.category}</option>
                    )}
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Age Group" hint="Determines which health score is treated as the target.">
                    <select
                      className="inp"
                      value={form.ageGroup || ""}
                      onChange={(e) => patchForm({ ageGroup: e.target.value })}
                      style={{ cursor: "pointer" }}
                    >
                      <option value="">Select age group</option>
                      {AGE_GROUPS.map(({ key, label }) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="Description" optional>
                    <textarea
                      className="inp"
                      rows={3}
                      value={form.description || ""}
                      onChange={(e) => patchForm({ description: e.target.value })}
                      placeholder="Short summary shown on the product detail page…"
                      style={{ resize: "vertical", lineHeight: 1.6 }}
                    />
                  </Field>
                </div>
              </div>
            </div>
          </div>

          {/* Product Images */}
          <div className="pw-card">
            <SectionHead
              icon={Package}
              title="Product Images"
              subtitle="Click a thumbnail to replace or remove it"
              count={imgs.length || undefined}
            />
            <div className="pw-sec-b">
              {imgs.length > 0 ? (
                <>
                  <div className="pw-stage" style={{ height: 250 }}>
                    {currentImg?.url ? (
                      <img
                        src={currentImg.url}
                        alt={currentImg.fileName || "Product scan"}
                        style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "zoom-in" }}
                        onLoad={() => setImgLoading(false)}
                        onError={() => setImgLoading(false)}
                        onClick={() => setLightboxOpen(true)}
                      />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 52, lineHeight: 1 }}>{currentImg?.emoji || "📦"}</span>
                        <span style={{ fontSize: 11.5, color: "#37BEB0", fontWeight: 600 }}>
                          {currentImg?.fileName || "No image for this slot"}
                        </span>
                      </div>
                    )}

                    {currentImg?.fileType && (
                      <span className="pw-stage-tag">{currentImg.fileType.replace(/_/g, " ")}</span>
                    )}

                    {currentImg?.url && (
                      <button type="button" className="pw-zoom-btn" title="Open fullscreen" onClick={() => setLightboxOpen(true)}>
                        <ZoomIn size={15} />
                      </button>
                    )}

                    {imgs.length > 1 && (
                      <>
                        <button type="button" className="carousel-btn" style={{ left: 8 }} aria-label="Previous image" onClick={() => setImgI((i) => (i - 1 + imgs.length) % imgs.length)}>
                          <ChevronLeft size={14} />
                        </button>
                        <button type="button" className="carousel-btn" style={{ right: 8 }} aria-label="Next image" onClick={() => setImgI((i) => (i + 1) % imgs.length)}>
                          <ChevronRight size={14} />
                        </button>
                        <span className="pw-stage-pill">{imgI + 1} / {imgs.length}</span>
                      </>
                    )}

                    {imgLoading && (
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(248,253,253,.85)", zIndex: 1, pointerEvents: "none" }}>
                        <span className="spin" style={{ display: "inline-block", width: 26, height: 26, border: "2.5px solid rgba(12,97,112,.1)", borderTop: "2.5px solid #37BEB0", borderRadius: "50%" }} />
                      </div>
                    )}
                  </div>

                  <div className="pw-thumbs">
                    {imgs.map((img, i) => (
                      <button
                        key={img.fileId || i}
                        type="button"
                        className={`pw-thumb${i === imgI ? " pw-thumb-a" : ""}${!img.url ? " pw-thumb-empty" : ""}`}
                        onClick={() => { setImgI(i); setSelectedThumbnailIndex(i); }}
                        title={`${img.fileName || img.fileType?.replace(/_/g, " ") || `Image ${i + 1}`} — click to edit`}
                        aria-label={`Edit image ${i + 1}`}
                      >
                        {img.url ? <img src={img.url} alt="" /> : <span>{img.emoji || "＋"}</span>}
                        <span className="pw-thumb-ov"><Pencil size={13} /></span>
                      </button>
                    ))}
                  </div>

                  {photos.some((ph) => ph.fileObject) && (
                    <div className="pw-note" style={{ marginTop: 12, background: "#fffbeb", borderColor: "#fde68a", display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <Upload size={14} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div style={{ fontSize: 12.5, color: "#92400e", lineHeight: 1.5 }}>
                        New photos are staged locally. They upload when you press <strong>Save Changes</strong>.
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="upload-zone">
                  <Package size={30} color="#A4E5E0" />
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "#0C6170" }}>No images available</div>
                  <div style={{ fontSize: 12, color: "#37BEB0" }}>Label scans appear here once the agent submits them.</div>
                </div>
              )}
            </div>
          </div>

          {/* Health Scores by Age Group */}
          <div className="pw-card">
            <SectionHead
              icon={Sparkles}
              title="Health Scores by Age Group"
              subtitle="Enter a rating from 0 to 100 for each bracket"
            />
            <div className="pw-sec-b">
              <div className="pw-ages">
                {SCORE_CARD_GROUPS.map(({ keys, label }) => {
                  const groupKey = keys[0];
                  const isTarget = keys.includes(form.ageGroup);
                  const val = ageGroupScores[groupKey] ?? "";
                  const bad = isBadScore(val);
                  const targetOrder = SCORE_ORDER_INDEX[form.ageGroup];
                  const groupOrder = SCORE_ORDER_INDEX[groupKey];
                  const isLocked = targetOrder != null && groupOrder != null && groupOrder < targetOrder;

                  return (
                    <div key={groupKey} className={`pw-age${isTarget ? " pw-age-t" : ""}`}>
                      <div className="flex items-center justify-between gap-1.5">
                        <label htmlFor={`score-${groupKey}`} className="pw-age-lbl">{label}</label>
                        {isTarget && <span className="pw-tgt">Target</span>}
                      </div>

                      <div className="pw-suffix">
                        <input
                          id={`score-${groupKey}`}
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          className={`inp inp-sm${bad ? " inp-err" : ""}`}
                          placeholder="—"
                          value={val}
                          onChange={(e) => {
                            if (isLocked) {
                              const targetLabel = AGE_GROUPS.find((g) => g.key === form.ageGroup)?.label || form.ageGroup;
                              addToast(`${label} is locked — it's younger than the selected target Age Group (${targetLabel}), so its score can't be edited independently.`, "error");
                              return;
                            }
                            setScore(keys, e.target.value);
                          }}
                          style={{ fontWeight: 700, color: bad ? "#d94f4f" : "#062830", opacity: isLocked ? 0.55 : 1, cursor: isLocked ? "not-allowed" : "text" }}
                          title={isLocked ? "Locked — younger than the selected target Age Group" : undefined}
                        />
                        <span>/ 100</span>
                      </div>

                      <div className="pw-meter">
                        <i style={{ width: `${Math.max(0, Math.min(100, Number(val) || 0))}%`, background: scoreColor(val) }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {invalidScoreCount > 0 && (
                <div className="pw-hint pw-hint-err" style={{ marginTop: 12 }}>
                  {invalidScoreCount} score{invalidScoreCount > 1 ? "s are" : " is"} outside the allowed 0–100 range. Fix before saving.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Right Column: Ingredients & Nutrition ────────────────────────── */}
        <div className="flex flex-col gap-4 min-w-0">

          {/* Ingredients */}
          <div className="pw-card">
            <SectionHead
              icon={List}
              title="Ingredients List"
              subtitle={concernCount > 0 ? `${concernCount} flagged with a health concern` : "In the order printed on the label"}
              count={ingredientsList.length}
              action={
                <button type="button" className="btn btn-g btn-xs" onClick={addIngredient}>
                  <Plus size={13} /> Add
                </button>
              }
            />

            <div className="pw-sec-b">
              {ingredientsList.length === 0 ? (
                <div className="upload-zone">
                  <List size={26} color="#A4E5E0" />
                  <div style={{ fontSize: 13, color: "#0C6170", fontWeight: 600 }}>No ingredients yet</div>
                  <button type="button" className="btn btn-g btn-xs" onClick={addIngredient}>
                    <Plus size={13} /> Add the first ingredient
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {ingredientsList.map((ing, index) => {
                    const hasConcern = !!ing.detail_concern?.trim();
                    const concernOpen = hasConcern || openConcerns[ing.id];

                    return (
                      <div key={ing.id} className={`pw-row${hasConcern ? " pw-warn" : ""}`}>
                        {/* Name + remove */}
                        <div className="flex items-center gap-2">
                          <span className="pw-idx">{index + 1}</span>
                          <input
                            className="inp inp-sm"
                            placeholder="Ingredient name — e.g. Rice Flakes"
                            value={ing.name}
                            onChange={(e) => updateIngredient(ing.id, "name", e.target.value)}
                            style={{ flex: 1, fontWeight: 600, minWidth: 0 }}
                          />
                          <button
                            type="button"
                            className="pw-icon-btn"
                            onClick={() => removeIngredient(ing.id)}
                            title="Remove ingredient"
                            aria-label={`Remove ingredient ${index + 1}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>

                        {/* Quantity */}
                        <input
                          className="inp inp-sm"
                          placeholder="Quantity or estimation — e.g. Estimated >50%"
                          value={ing.quantity || ""}
                          onChange={(e) => updateIngredient(ing.id, "quantity", e.target.value)}
                          style={{ marginTop: 8, fontSize: 12.5, color: "#0C6170" }}
                        />

                        {/* Optional concern note */}
                        {concernOpen ? (
                          <div style={{ marginTop: 8 }}>
                            <div className="flex items-center justify-between" style={{ marginBottom: 5 }}>
                              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: hasConcern ? "#b45309" : "#37BEB0", display: "inline-flex", alignItems: "center", gap: 5 }}>
                                <TriangleAlert size={11} /> Health concern
                              </span>
                              <button
                                type="button"
                                className="pw-ghost"
                                style={{ padding: "2px 7px", fontSize: 11 }}
                                onClick={() => {
                                  updateIngredient(ing.id, "detail_concern", "");
                                  setOpenConcerns((p) => ({ ...p, [ing.id]: false }));
                                }}
                              >
                                <X size={11} /> Clear
                              </button>
                            </div>
                            <textarea
                              className="inp inp-sm"
                              rows={2}
                              placeholder="Why this ingredient is a concern for the target age group…"
                              value={ing.detail_concern || ""}
                              onChange={(e) => updateIngredient(ing.id, "detail_concern", e.target.value)}
                              style={{
                                fontSize: 12.5,
                                lineHeight: 1.55,
                                resize: "vertical",
                                color: hasConcern ? "#92400e" : "#062830",
                                background: hasConcern ? "#fffdf5" : "#fff",
                                borderColor: hasConcern ? "#fde68a" : undefined,
                              }}
                            />
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="pw-ghost"
                            style={{ marginTop: 8 }}
                            onClick={() => setOpenConcerns((p) => ({ ...p, [ing.id]: true }))}
                          >
                            <Plus size={11} /> Add health concern
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Nutrition Facts */}
          <div className="pw-card">
            <SectionHead
              icon={BarChart2}
              title="Nutritional Information"
              subtitle="Values as printed, per the stated basis"
              count={nutritionFactsList.length}
              action={
                <button type="button" className="btn btn-g btn-xs" onClick={() => addNutrient()}>
                  <Plus size={13} /> Add
                </button>
              }
            />

            <div className="pw-sec-b">
              {nutrientPresets.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#37BEB0", marginBottom: 7 }}>
                    Quick add
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {nutrientPresets.slice(0, 8).map((s) => (
                      <button key={s.key} type="button" className="pw-ghost" onClick={() => addNutrient(s)}>
                        <Plus size={11} /> {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {nutritionFactsList.length === 0 ? (
                <div className="upload-zone">
                  <BarChart2 size={26} color="#A4E5E0" />
                  <div style={{ fontSize: 13, color: "#0C6170", fontWeight: 600 }}>No nutrition rows yet</div>
                  <div style={{ fontSize: 12, color: "#37BEB0" }}>Use “Quick add” above or add a custom row.</div>
                </div>
              ) : (
                <>
                  {/* Desktop / tablet: table layout */}
                  <div className="tbl-wrap hidden sm:block">
                    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 6px" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "0 6px 4px", color: "#37BEB0", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em" }}>Nutrient</th>
                          <th style={{ textAlign: "left", padding: "0 6px 4px", color: "#37BEB0", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", width: 96 }}>Value</th>
                          <th style={{ textAlign: "left", padding: "0 6px 4px", color: "#37BEB0", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", width: 84 }}>Unit</th>
                          <th style={{ textAlign: "left", padding: "0 6px 4px", color: "#37BEB0", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", width: 96 }}>Basis</th>
                          <th style={{ width: 42 }} aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {nutritionFactsList.map((row) => (
                          <tr key={row.id}>
                            <td style={{ padding: "0 6px" }}>
                              <input
                                className="inp inp-sm"
                                list="nutrient-suggestions"
                                value={row.label}
                                onChange={(e) => updateNutrient(row.id, "label", e.target.value)}
                                placeholder="e.g. Energy"
                                style={{ width: "100%", fontWeight: 600 }}
                              />
                            </td>
                            <td style={{ padding: "0 6px" }}>
                              <input
                                className="inp inp-sm"
                                type="text"
                                inputMode="decimal"
                                value={row.value}
                                onChange={(e) => updateNutrient(row.id, "value", e.target.value)}
                                placeholder="0"
                                style={{ width: "100%", fontWeight: 700, color: "#0C6170" }}
                              />
                            </td>
                            <td style={{ padding: "0 6px" }}>
                              <input
                                className="inp inp-sm"
                                list="unit-suggestions"
                                value={row.unit}
                                onChange={(e) => updateNutrient(row.id, "unit", e.target.value)}
                                placeholder="g"
                                style={{ width: "100%" }}
                              />
                            </td>
                            <td style={{ padding: "0 6px" }}>
                              <input
                                className="inp inp-sm"
                                list="basis-suggestions"
                                value={row.basis}
                                onChange={(e) => updateNutrient(row.id, "basis", e.target.value)}
                                placeholder="100g"
                                style={{ width: "100%" }}
                              />
                            </td>
                            <td style={{ padding: "0 6px", textAlign: "center" }}>
                              <button
                                type="button"
                                className="pw-icon-btn"
                                onClick={() => removeNutrient(row.id)}
                                title="Remove row"
                                aria-label={`Remove ${row.label || "nutrient"}`}
                              >
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: stacked cards so nothing gets squeezed */}
                  <div className="flex flex-col gap-2.5 sm:hidden">
                    {nutritionFactsList.map((row, i) => (
                      <div key={row.id} className="pw-row">
                        <div className="flex items-center gap-2">
                          <span className="pw-idx">{i + 1}</span>
                          <input
                            className="inp inp-sm"
                            list="nutrient-suggestions"
                            value={row.label}
                            onChange={(e) => updateNutrient(row.id, "label", e.target.value)}
                            placeholder="Nutrient"
                            style={{ flex: 1, fontWeight: 600, minWidth: 0 }}
                          />
                          <button
                            type="button"
                            className="pw-icon-btn"
                            onClick={() => removeNutrient(row.id)}
                            aria-label={`Remove ${row.label || "nutrient"}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2" style={{ marginTop: 8 }}>
                          <input
                            className="inp inp-sm"
                            inputMode="decimal"
                            value={row.value}
                            onChange={(e) => updateNutrient(row.id, "value", e.target.value)}
                            placeholder="Value"
                            style={{ fontWeight: 700, color: "#0C6170" }}
                          />
                          <input
                            className="inp inp-sm"
                            list="unit-suggestions"
                            value={row.unit}
                            onChange={(e) => updateNutrient(row.id, "unit", e.target.value)}
                            placeholder="Unit"
                          />
                          <input
                            className="inp inp-sm"
                            list="basis-suggestions"
                            value={row.basis}
                            onChange={(e) => updateNutrient(row.id, "basis", e.target.value)}
                            placeholder="Basis"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Shared input suggestions */}
      <datalist id="nutrient-suggestions">
        {COMMON_NUTRIENT_SUGGESTIONS.map((s) => <option key={s.key} value={s.label} />)}
      </datalist>
      <datalist id="unit-suggestions">
        {UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}
      </datalist>
      <datalist id="basis-suggestions">
        {BASIS_SUGGESTIONS.map((b) => <option key={b} value={b} />)}
      </datalist>

      <ConfirmModal {...confirmM} onClose={() => setConfirmM({ open: false })} />

      {/* Hidden file input for thumbnail replacement */}
      <input
        type="file"
        id="thumbnail-photo-replace"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleReplaceThumbnailFile}
      />

      {/* Thumbnail edit/replace/delete modal */}
      {selectedThumbnailIndex !== null && (
        <div className="modal-overlay" onClick={() => setSelectedThumbnailIndex(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, padding: 0, overflow: "hidden" }}>
            <div className="pw-sec-h">
              <div className="pw-sec-icn"><Upload size={15} /></div>
              <div className="flex-1 min-w-0">
                <div className="pw-sec-t">Edit Photo #{selectedThumbnailIndex + 1}</div>
                <div className="pw-sec-sub">
                  {photos[selectedThumbnailIndex]?.fileType?.replace(/_/g, " ") || "unknown file type"}
                </div>
              </div>
              <button className="pw-icon-btn" onClick={() => setSelectedThumbnailIndex(null)} aria-label="Close">
                <X size={14} />
              </button>
            </div>

            <div style={{ padding: 18 }}>
              <div className="pw-stage" style={{ height: 150, marginBottom: 16 }}>
                {photos[selectedThumbnailIndex]?.url ? (
                  <img
                    src={photos[selectedThumbnailIndex].url}
                    alt="Thumbnail preview"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <div style={{ fontSize: 12.5, color: "#A4E5E0", fontWeight: 600 }}>No image in this slot</div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-p"
                  onClick={() => document.getElementById("thumbnail-photo-replace").click()}
                  style={{ justifyContent: "center" }}
                >
                  <Upload size={14} /> {photos[selectedThumbnailIndex]?.url ? "Replace image" : "Upload image"}
                </button>

                {photos[selectedThumbnailIndex]?.url && (
                  <button
                    type="button"
                    className="btn btn-d"
                    onClick={handleDeleteThumbnail}
                    style={{ justifyContent: "center" }}
                  >
                    <Trash2 size={14} /> Delete photo
                  </button>
                )}

                <button className="btn btn-g" onClick={() => setSelectedThumbnailIndex(null)} style={{ justifyContent: "center" }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen photo preview */}
      {lightboxOpen && currentImg?.url && (
        <div className="pw-lb" onClick={() => setLightboxOpen(false)} role="dialog" aria-modal="true">
          <div className="pw-lb-h" onClick={(e) => e.stopPropagation()}>
            <div className="min-w-0">
              <div className="pw-lb-t">{currentImg.fileName?.replace(/_/g, " ") || "Image preview"}</div>
              <div style={{ fontSize: 11.5, color: "rgba(219,245,240,.6)", fontWeight: 600 }}>
                {imgI + 1} of {imgs.length} · Esc to close
              </div>
            </div>
            <button className="pw-lb-btn" aria-label="Close" onClick={() => setLightboxOpen(false)}>
              <X size={17} />
            </button>
          </div>
          <div className="pw-lb-body" onClick={(e) => e.stopPropagation()}>
            <img src={currentImg.url} alt={currentImg.fileName || "Product photo"} />
          </div>
        </div>
      )}

      {/* Mobile sticky action bar */}
      <div className="pw-bar md:hidden">
        <button type="button" className="btn btn-g" onClick={handleCancelClick} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-p" onClick={handleSaveClick} disabled={saveDisabled}>
          <Save size={15} />{saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
